import { GroundTransportDateTimeState } from "components/AppointmentDatePicker/GroundTransportDateTime";
import { GooglePlaceInputValue } from "components/ValidatedFields/GooglePlaceInput";
import moment from "moment";
import React from "react";
import { getAppointmentPhase } from "utilities";
import { getGroundTransportType, getPickupLocationType } from "utilities/enumutils";

export interface GroundTransportInfo {
  artist: server.dto.ArtistSmall
  phase: server.dto.AppointmentPhase
  travellers: server.dto.ContactSmallV2[]
  type: server.dto.GroundTransportType
  notes: string
  driver: server.dto.Driver

  startPickupLocationType: server.dto.PickupLocationType;
  startPickupLocationOther: string;

  endPickupLocationType: server.dto.PickupLocationType;
  endPickupLocationOther: string;

  appointmentDateTime: server.dto.AppointmentDateTime;
  date: GroundTransportDateTimeState
  durationInMinutes: number

  startPlace: GooglePlaceInputValue
  endPlace: GooglePlaceInputValue

  directions: server.dto.GoogleDirections
}

const mapGroundTransportToState = (groundTransport: server.dto.GroundTransport, travellers: server.dto.ContactSmallV2[]): GroundTransportInfo => ({
  artist: groundTransport.artist,
  phase: getAppointmentPhase(groundTransport.phase),
  travellers: travellers,
  type: groundTransport.groundTransportType === "unknown" ? null : getGroundTransportType(groundTransport.groundTransportType),
  notes: groundTransport.notes,
  driver: groundTransport.driver,

  startPickupLocationType: getPickupLocationType(groundTransport.startPickupLocationType),
  startPickupLocationOther: groundTransport.startPickupLocationOther,
  endPickupLocationType: getPickupLocationType(groundTransport.endPickupLocationType),
  endPickupLocationOther: groundTransport.endPickupLocationOther,

  appointmentDateTime: groundTransport.appointmentDateTime,
  date: {
    departArrive: groundTransport.departOrArrive,
    dateTime: groundTransport.departOrArrive === "departAt"
      ? moment.utc(groundTransport.appointmentDateTime.startDisplay)
      : moment.utc(groundTransport.appointmentDateTime.endDisplay),
    inTheNightOf: groundTransport.appointmentDateTime.mode === "hasTimeStartsInNight",
  },

  durationInMinutes: groundTransport.durationInMinutes,

  startPlace: {
    id: groundTransport.startGooglePlaceId,
    name: groundTransport.startGooglePlaceName,
    address: groundTransport.startGooglePlaceAddress,
    geo: groundTransport.startGooglePlaceGeo,
  },
  endPlace: {
    id: groundTransport.endGooglePlaceId,
    name: groundTransport.endGooglePlaceName,
    address: groundTransport.endGooglePlaceAddress,
    geo: groundTransport.endGooglePlaceGeo,
  },
  directions: groundTransport.directions,
});

export const useGroundTransportDetail = (
  groundTransport: server.dto.GroundTransport,
  travellers: server.dto.ContactSmallV2[],
  onArtistChanged: (newArtist: server.dto.ArtistSmall) => void,
  onDateChanged: (newDate: server.dto.AppointmentDateTime) => void,
  onStatusChanged: () => void,
) => {
  const [state, setState] = React.useState<GroundTransportInfo>(mapGroundTransportToState(groundTransport, travellers));

  React.useEffect(() => {
    setState(mapGroundTransportToState(groundTransport, travellers));
  }, [groundTransport, travellers]);

  const hasDriver = Boolean(state.driver.name || state.driver.mobile || state.driver.email || state.driver.vehicle);
  const hasMap = Boolean(state.startPlace.id || state.endPlace.id);

  const onValueChanged = (key: string, value: any) => {
    if (key === "artist") {
      setState({ ...state, artist: value });
    } else if (key === "travellers") {
      setState({ ...state, travellers: value });
    } else if (key === "status") {
      setState({ ...state, phase: value });
    } else if (key === "transportType") {
      setState({ ...state, type: value });
    } else if (key === "notes") {
      setState({ ...state, notes: value });
    } else if (key === "driver") {
      setState({ ...state, driver: value });
    } else if (key === "typeFrom") {
      setState({
        ...state,
        startPickupLocationType: value.pickupLocationType,
        startPickupLocationOther: value.pickupLocationOther,
      });
    } else if (key === "typeTo") {
      setState({
        ...state,
        endPickupLocationType: value.pickupLocationType,
        endPickupLocationOther: value.pickupLocationOther,
      });
    } else if (key === "addressFrom") {
      setState({ ...state, startPlace: value });
    } else if (key === "addressTo") {
      setState({ ...state, endPlace: value });
    } else if (key === "date") {
      setState({ ...state, date: value });
    } else if (key === "durationInMinutes") {
      setState({ ...state, durationInMinutes: value });
    }
  }

  const onSuccessfulStatusUpdate = () => {
    onStatusChanged();
  }

  const onSuccessfulDateUpdate = async () => {
    const newData = await window.CoreApi.GroundTransport.GetSidePanelData(groundTransport.groundTransportId);
    if (newData.data.hasErrors) {
      return;
    }
    onDateChanged(newData.data.data.groundTransport.appointmentDateTime);
    setState({
      ...state,
      appointmentDateTime: newData.data.data.groundTransport.appointmentDateTime,
      date: {
        departArrive: newData.data.data.groundTransport.departOrArrive,
        dateTime: newData.data.data.groundTransport.departOrArrive === "departAt"
          ? moment.utc(newData.data.data.groundTransport.appointmentDateTime.startDisplay)
          : moment.utc(newData.data.data.groundTransport.appointmentDateTime.endDisplay),
        inTheNightOf: newData.data.data.groundTransport.appointmentDateTime.mode === "hasTimeStartsInNight",
      },
      durationInMinutes: newData.data.data.groundTransport.durationInMinutes,
    });
  }

  const onSuccessfulAddressUpdate = async () => {
    const newData = await window.CoreApi.GroundTransport.GetSidePanelData(groundTransport.groundTransportId);
    if (newData.data.hasErrors) {
      return;
    }

    setState({
      ...state,
      startPlace: {
        id: newData.data.data.groundTransport.startGooglePlaceId,
        name: newData.data.data.groundTransport.startGooglePlaceName,
        address: newData.data.data.groundTransport.startGooglePlaceAddress,
        geo: newData.data.data.groundTransport.startGooglePlaceGeo,
      },
      endPlace: {
        id: newData.data.data.groundTransport.endGooglePlaceId,
        name: newData.data.data.groundTransport.endGooglePlaceName,
        address: newData.data.data.groundTransport.endGooglePlaceAddress,
        geo: newData.data.data.groundTransport.endGooglePlaceGeo,
      },
      durationInMinutes: newData.data.data.groundTransport.durationInMinutes,
      directions: newData.data.data.groundTransport.directions,
    });
  }

  const onSuccessfulArtistUpdate = async (newArtist: server.dto.ArtistSmall) => {
    const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
      companyId: newArtist.artistId,
      defaultSelect: true,
      isActive: true,
      page: 1,
      perPage: 1000,
    });

    if (result.data.hasErrors) {
      return;
    }

    const newTravellers = result.data.data.map(c => ({
      contactId: c.contact.contactId,
      culture: c.contact.culture,
      gender: c.contact.gender,
      isValidated: c.contact.isValidated,
      name: c.contact.name,
      position: c.contact.position,
      profileImageUrl: c.contact.profileImageUrl,
    }))

    const updateDetailsResults = await window.CoreApi.GroundTransport.UpdateDetails({
      ...getUpdateGroundTransportInstruction({
        ...state,
        artist: newArtist,
        travellers: newTravellers,
      }),
    });

    if (updateDetailsResults.data.hasErrors) {
      return;
    }

    setState({
      ...state,
      artist: newArtist,
      travellers: newTravellers,
    });

    onArtistChanged(newArtist);
  }

  const onSuccessfulUpdate = async (key: string, newValue: any) => {
    if (key === "status") {
      onSuccessfulStatusUpdate();
      return;
    }

    if (key === "addressFrom" || key === "addressTo") {
      await onSuccessfulAddressUpdate();
      return;
    }

    if (key === "artist") {
      await onSuccessfulArtistUpdate(newValue);
      return;
    }

    if (key === "date" || key === "durationInMinutes") {
      await onSuccessfulDateUpdate();
      return;
    }
  }

  const getUpdateGroundTransportInstruction = (gt: GroundTransportInfo): server.dto.UpdateGroundTransportInstruction => ({
    artistId: gt.artist.artistId,
    dateTime: gt.date.dateTime.toDate(),
    departOrArrive: gt.date.departArrive,
    driver: {
      name: gt.driver.name || null,
      mobile: gt.driver.mobile || null,
      email: gt.driver.email || null,
      vehicle: gt.driver.vehicle || null,
    },
    durationInMinutes: gt.durationInMinutes,
    endGooglePlaceAddress: gt.endPlace.address,
    endGooglePlaceGeo: gt.endPlace.geo,
    endGooglePlaceId: gt.endPlace.id,
    endGooglePlaceName: gt.endPlace.name,
    endPickupLocation: {
      value: gt.endPickupLocationType.value,
      other: gt.endPickupLocationOther,
    },
    groundTransportId: groundTransport.groundTransportId,
    groundTransportType: gt.type === null ? "unknown" : gt.type.value,
    inTheNightOf: gt.date.inTheNightOf,
    notes: gt.notes,
    phase: gt.phase.value,

    startGooglePlaceAddress: gt.startPlace.address,
    startGooglePlaceGeo: gt.startPlace.geo,
    startGooglePlaceId: gt.startPlace.id,
    startGooglePlaceName: gt.startPlace.name,
    startPickupLocation: {
      value: gt.startPickupLocationType.value,
      other: gt.startPickupLocationOther,
    },
    travellers: gt.travellers.map(c => c.contactId),
  });

  return {
    state,
    hasDriver,
    hasMap,
    onValueChanged,
    onSuccessfulUpdate,
    getUpdateGroundTransportInstruction,
  }
}